<template>
  <div class="manager-dashboard">
    <div class="header">
      <h2>{{ $t('roles.manager') }} {{ $t('navigation.dashboard') }}</h2>
      <div class="header-actions">
        <button @click="$router.push('/company-settings')" class="settings-btn">
          ⚙️ {{ $t('navigation.settings') }}
        </button>
        <button @click="$router.push('/shifts')" class="create-shift-btn">
          🗓️ {{ $t('navigation.shiftCalendar') }}
        </button>
        <button @click="exportAllReports" class="export-btn" :disabled="loading">
          📄 {{ $t('common.export') }}
        </button>
        <button @click="refreshData" class="refresh-btn" :disabled="loading">
          🔄 {{ $t('common.search') }}
        </button>
      </div>
    </div>

    <!-- Summary Stats -->
    <div class="stats-summary">
      <div class="stat-card">
        <span class="stat-label">{{ $t('status.pending') }}</span>
        <span class="stat-value">{{ pendingShiftsCount }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">{{ $t('timeReporting.reportedHours') }}</span>
        <span class="stat-value">{{ todaySubmittedCount }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">{{ $t('timeReporting.totalHours') }} ({{ $t('status.pending') }})</span>
        <span class="stat-value">{{ totalPendingHours }}{{ $t('common.hours') }}</span>
      </div>
      <div class="stat-card sick-leave" @click="showSickLeaveModal = true" style="cursor: pointer;">
        <span class="stat-label">🤒 {{ $t('userManagement.sickLeave') }}</span>
        <span class="stat-value">{{ upcomingSickLeavesCount }}</span>
      </div>
      <div class="stat-card rejected">
        <span class="stat-label">{{ $t('status.rejected') }}</span>
        <span class="stat-value">{{ rejectedShiftsCount }}</span>
      </div>
    </div>

    <!-- Filter Section -->
    <div class="filters">
      <div class="filter-group">
        <label for="status-filter">{{ $t('common.search') }}:</label>
        <select v-model="statusFilter" id="status-filter" @change="filterShifts">
          <option value="pending">⏳ {{ $t('status.pending') }}</option>
          <option value="submitted_pending">⚠️ {{ $t('status.submitted_pending') }}</option>
          <option value="submitted_ok">✅ {{ $t('status.submitted_ok') }}</option>
          <option value="rejected">❌ {{ $t('status.rejected') }}</option>
          <option value="approved">✅ {{ $t('status.approved') }}</option>
          <option value="all">{{ $t('status.all') }}</option>
        </select>
      </div>
      
      <div class="filter-group">
        <label for="date-range">{{ $t('pdf.date') }}:</label>
        <select v-model="dateRange" id="date-range" @change="loadShifts">
          <option value="week">{{ $t('weekdays.week') }}</option>
          <option value="month">{{ $t('weekdays.month') }}</option>
          <option value="all">{{ $t('status.all') }}</option>
        </select>
      </div>

      <div class="filter-group" v-if="filteredShifts.length > 0">
        <button @click="bulkApproveAll" class="bulk-approve-btn" :disabled="loading">
          ✅ {{ $t('status.approve') }} {{ $t('status.pending') }} ({{ pendingShiftsCount }})
        </button>
      </div>
    </div>

    <!-- Pending Shifts List -->
    <div class="shifts-section">
      <h3>{{ $t('timeReporting.timeReportApproval') }}</h3>
      
      <div v-if="loading" class="loading">
        {{ $t('common.loading') }}
      </div>

      <div v-else-if="filteredShifts.length === 0" class="no-data">
        {{ $t('timeReporting.noTimeReports') }}
      </div>

      <div v-else class="shifts-list">
        <div 
          v-for="shift in filteredShifts" 
          :key="shift.id" 
          class="shift-card"
          :class="{ 
            'status-submitted-pending': shift.status === 'submitted_pending',
            'status-submitted-ok': shift.status === 'submitted_ok',
            'status-approved': shift.status === 'approved',
            'status-rejected': shift.status === 'rejected',
            'has-discrepancy': shift.status === 'submitted_pending'
          }"
        >
          <div class="shift-header">
            <div class="worker-info">
              <h4>{{ shift.user.first_name }} {{ shift.user.last_name }}</h4>
              <span class="role-badge">{{ shift.user.role }}</span>
            </div>
            
            <div class="shift-date">
              <span class="date">{{ formatDate(shift.date) }}</span>
              <span class="weekday">{{ formatWeekday(shift.date) }}</span>
            </div>
            
            <div class="shift-times">
              <span class="time-range">{{ shift.start_time }} - {{ shift.end_time }}</span>
              <span class="shift-hours">{{ calculateShiftHours(shift) }}h</span>
            </div>
            
            <div class="shift-status">
              <span 
                class="status-badge" 
                :class="shift.status"
                @mouseenter="shift.status === 'submitted_pending' ? showDiscrepancyModal(shift) : null"
                @mouseleave="hoveredShiftId = null"
              >
                {{ shift.status === 'submitted_pending' ? '⚠️ ' + $t('status.submitted_pending') : 
                   shift.status === 'submitted_ok' ? '✅ ' + $t('status.submitted_ok') : 
                   shift.status === 'approved' ? $t('status.approved') : 
                   shift.status === 'rejected' ? $t('status.rejected') : shift.status }}
              </span>
              <span class="submitted-time">{{ formatDateTime(shift.updated_at) }}</span>
              
              <!-- Hover Modal for Discrepancies -->
              <div 
                v-if="shift.status === 'submitted_pending' && shift.mismatchDetails && hoveredShiftId === shift.id" 
                class="discrepancy-modal"
                @mouseenter="hoveredShiftId = shift.id"
                @mouseleave="hoveredShiftId = null"
              >
                <div class="modal-arrow"></div>
                <div class="modal-content">
                  <div class="modal-header">
                    <span class="warning-icon">⚠️</span>
                    <strong>Review Required</strong>
                  </div>
                  
                  <!-- Show time mismatch if it exists -->
                  <div v-if="shift.mismatchDetails.hasShiftMismatch" class="modal-section shift-mismatch">
                    <div class="section-title">⏰ Time Mismatch</div>
                    <div class="section-details">
                      <div v-if="shift.mismatchDetails.assignedStart" class="detail-row">
                        <span class="label">Assigned:</span>
                        <span class="value">{{ shift.mismatchDetails.assignedStart }} - {{ shift.mismatchDetails.assignedEnd }} ({{ shift.mismatchDetails.assignedDuration }} min)</span>
                      </div>
                      <div class="detail-row">
                        <span class="label">Reported:</span>
                        <span class="value highlight">{{ shift.mismatchDetails.reportedStart }} - {{ shift.mismatchDetails.reportedEnd }} ({{ shift.mismatchDetails.reportedDuration }} min)</span>
                      </div>
                      <div v-if="shift.mismatchDetails.assignedStart && Math.abs(shift.mismatchDetails.reportedDuration - shift.mismatchDetails.assignedDuration) > 5" class="detail-row difference">
                        <span class="label">⚠️ Difference:</span>
                        <span class="value">{{ Math.abs(shift.mismatchDetails.reportedDuration - shift.mismatchDetails.assignedDuration) }} minutes 
                          {{ shift.mismatchDetails.reportedDuration > shift.mismatchDetails.assignedDuration ? 'MORE' : 'LESS' }} than assigned</span>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Show break mismatch if it exists -->
                  <div v-if="shift.mismatchDetails.hasBreakDiscrepancy" class="modal-section break-discrepancy">
                    <div class="section-title">☕ Break Mismatch</div>
                    <div class="section-details">
                      <div class="detail-row">
                        <span class="label">Required:</span>
                        <span class="value">{{ shift.mismatchDetails.requiredBreak }} minutes</span>
                      </div>
                      <div class="detail-row difference">
                        <span class="label">Reported:</span>
                        <span class="value highlight">{{ shift.mismatchDetails.reportedBreak }} minutes</span>
                      </div>
                      <div class="detail-row difference">
                        <span class="label">⚠️ Issue:</span>
                        <span class="value">
                          {{ shift.mismatchDetails.reportedBreak < shift.mismatchDetails.requiredBreak 
                             ? `Missing ${shift.mismatchDetails.requiredBreak - shift.mismatchDetails.reportedBreak} minutes` 
                             : `${shift.mismatchDetails.reportedBreak - shift.mismatchDetails.requiredBreak} minutes extra` }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="shift-actions">
              <button 
                @click="approveShift(shift)" 
                class="action-btn approve"
                :disabled="loading || shift.status === 'approved'"
                v-if="shift.status !== 'approved'"
              >
                ✅ {{ $t('status.approve') }}
              </button>
              <button 
                @click="rejectShift(shift)" 
                class="action-btn reject"
                :disabled="loading || shift.status === 'rejected'"
                v-if="shift.status !== 'rejected'"
              >
                ❌ {{ $t('status.reject') }}
              </button>
              <button 
                @click="viewWorkerDetails(shift)" 
                class="action-btn view"
                :disabled="loading"
              >
                👁️ {{ $t('common.search') }}
              </button>
            </div>
          </div>

          <div class="shift-details" v-if="shift.description">
            <p class="description">{{ $t('timeReporting.description') }}: {{ shift.description }}</p>
          </div>

          <div class="shift-breakdown" v-if="hasOBHours(shift)">
            <div class="ob-hours">
              <span v-if="getOB1Hours(shift) > 0" class="ob1">{{ $t('pdf.ob1') }}: {{ getOB1Hours(shift) }}h</span>
              <span v-if="getOB2Hours(shift) > 0" class="ob2">{{ $t('pdf.ob2') }}: {{ getOB2Hours(shift) }}h</span>
              <span v-if="getOB3Hours(shift) > 0" class="ob3">{{ $t('pdf.ob3') }}: {{ getOB3Hours(shift) }}h</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error/Success Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>

    <!-- Sick Leave Modal -->
    <div v-if="showSickLeaveModal" class="modal-overlay" @click="showSickLeaveModal = false">
      <div class="modal-content sick-leave-modal" @click.stop>
        <div class="modal-header">
          <h3>🤒 {{ $t('userManagement.sickLeave') }}</h3>
          <button @click="showSickLeaveModal = false" class="close-btn">✕</button>
        </div>
        <div class="modal-body">
          <div v-if="loadingSickLeaves" class="loading">{{ $t('common.loading') }}...</div>
          <div v-else-if="upcomingSickLeaves.length === 0" class="no-data">
            {{ $t('timeReporting.noSickLeaves') }}
          </div>
          <div v-else class="sick-leave-list">
            <div 
              v-for="sickLeave in upcomingSickLeaves" 
              :key="sickLeave.id"
              class="sick-leave-item"
            >
              <div class="sick-worker-info">
                <span class="worker-name">{{ sickLeave.user.first_name }} {{ sickLeave.user.last_name }}</span>
                <span class="sick-date">{{ formatDate(sickLeave.date) }}</span>
              </div>
              <div class="sick-leave-actions">
                <span class="sick-icon">🤒</span>
                <button 
                  @click="removeSickLeave(sickLeave)" 
                  class="remove-sick-leave-btn"
                  title="Remove sick leave"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showConfirmModal" class="modal-overlay" @click="showConfirmModal = false">
      <div class="modal-content confirm-modal" @click.stop>
        <div class="modal-header">
          <h3>{{ confirmModalData.title }}</h3>
        </div>
        <div class="modal-body">
          <p>{{ confirmModalData.message }}</p>
        </div>
        <div class="modal-footer">
          <button @click="showConfirmModal = false" class="btn-secondary">
            {{ $t('common.cancel') }}
          </button>
          <button @click="confirmAction" class="btn-danger">
            {{ $t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth.js'
import { timeEntryApi } from '../services/timeEntryApi.js'
import { shiftApi } from '../services/shiftApi.js'
import { sickLeaveApi } from '../services/sickLeaveApi.js'

const router = useRouter()
const authStore = useAuthStore()
const loading = ref(false)
const message = ref('')
const messageType = ref('success')
const allShifts = ref([])
const statusFilter = ref('pending')
const dateRange = ref('week')
const hoveredShiftId = ref(null)
const showSickLeaveModal = ref(false)
const upcomingSickLeaves = ref([])
const loadingSickLeaves = ref(false)
const showConfirmModal = ref(false)
const confirmModalData = ref({
  title: '',
  message: '',
  onConfirm: null
})

// Computed properties
const filteredShifts = computed(() => {
  let filtered = allShifts.value

  // Filter by status
  if (statusFilter.value === 'pending') {
    filtered = filtered.filter(shift => 
      shift.status === 'submitted_pending' || shift.status === 'submitted_ok'
    )
  } else if (statusFilter.value !== 'all') {
    filtered = filtered.filter(shift => shift.status === statusFilter.value)
  }

  // Sort by date (newest first, then by time)
  return filtered.sort((a, b) => {
    const dateCompare = new Date(b.date) - new Date(a.date)
    if (dateCompare !== 0) return dateCompare
    
    // If same date, sort by start time (earliest first)
    return a.start_time.localeCompare(b.start_time)
  })
})

const pendingShiftsCount = computed(() => {
  return allShifts.value.filter(shift => 
    shift.status === 'submitted_pending' || shift.status === 'submitted_ok'
  ).length
})

const todaySubmittedCount = computed(() => {
  const today = new Date().toISOString().split('T')[0]
  return allShifts.value.filter(shift => 
    shift.date === today && (shift.status === 'submitted_pending' || shift.status === 'submitted_ok')
  ).length
})

const totalPendingHours = computed(() => {
  const pendingShifts = allShifts.value.filter(shift => 
    shift.status === 'submitted_pending' || shift.status === 'submitted_ok'
  )
  const total = pendingShifts.reduce((sum, shift) => {
    return sum + parseFloat(calculateShiftHours(shift))
  }, 0)
  return total.toFixed(1)
})

const uniqueWorkersCount = computed(() => {
  const pendingShifts = allShifts.value.filter(shift => 
    shift.status === 'submitted_pending' || shift.status === 'submitted_ok'
  )
  const uniqueWorkers = new Set(pendingShifts.map(shift => shift.user_id))
  return uniqueWorkers.size
})

const rejectedShiftsCount = computed(() => {
  return allShifts.value.filter(shift => shift.status === 'rejected').length
})

const upcomingSickLeavesCount = computed(() => {
  return upcomingSickLeaves.value.length
})

onMounted(() => {
  // Check if user is authenticated
  if (!authStore.isAuthenticated) {
    router.push('/')
    return
  }
  loadShifts()
  loadUpcomingSickLeaves()
})

async function loadUpcomingSickLeaves() {
  try {
    loadingSickLeaves.value = true
    upcomingSickLeaves.value = []
    
    // Define date range (today to 7 days from now)
    const today = new Date()
    const sevenDaysLater = new Date()
    sevenDaysLater.setDate(today.getDate() + 7)
    
    const todayStr = today.toISOString().split('T')[0]
    const endDateStr = sevenDaysLater.toISOString().split('T')[0]
    
    // Get all sick leaves in the date range (no worker_id filter)
    const allSickLeaves = await sickLeaveApi.getSickLeaves({
      start_date: todayStr,
      end_date: endDateStr
    })
    
    if (!allSickLeaves || allSickLeaves.length === 0) {
      return
    }
    
    // Group sick leaves by worker
    const sickLeavesByWorker = {}
    for (const sickLeave of allSickLeaves) {
      const workerId = sickLeave.worker
      if (!sickLeavesByWorker[workerId]) {
        sickLeavesByWorker[workerId] = []
      }
      sickLeavesByWorker[workerId].push(sickLeave)
    }
    
    // Fetch user details for workers with sick leaves
    const workerIds = Object.keys(sickLeavesByWorker)
    
    const userPromises = workerIds.map(async (workerId) => {
      try {
        const response = await authStore.makeAuthenticatedRequest(`/api/users/${workerId}/`)
        const user = JSON.parse(await response.text())
        return user
      } catch (error) {
        console.error(`Failed to fetch user ${workerId}:`, error)
        return null
      }
    })
    
    const usersWithNulls = await Promise.all(userPromises)
    const users = usersWithNulls.filter(u => u !== null)
    
    // Flatten into array of sick leave entries with user info
    const sickLeaves = []
    users.forEach((user) => {
      const userSickLeaves = sickLeavesByWorker[user.id]
      if (userSickLeaves && Array.isArray(userSickLeaves)) {
        userSickLeaves.forEach(sl => {
          sickLeaves.push({
            ...sl,
            date: sl.start_date,
            user: {
              first_name: user.first_name,
              last_name: user.last_name,
              id: user.id
            }
          })
        })
      }
    })
    
    // Sort by date (soonest first)
    upcomingSickLeaves.value = sickLeaves.sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    )
  } catch (error) {
    console.error('Error loading upcoming sick leaves:', error)
    upcomingSickLeaves.value = []
  } finally {
    loadingSickLeaves.value = false
  }
}

function removeSickLeave(sickLeave) {
  confirmModalData.value = {
    title: 'Remove Sick Leave',
    message: `Remove sick leave for ${sickLeave.user.first_name} ${sickLeave.user.last_name} on ${formatDate(sickLeave.date)}?`,
    onConfirm: async () => {
      try {
        await sickLeaveApi.deleteSickLeave(sickLeave.id)
        showMessage('Sick leave removed successfully', 'success')
        await loadUpcomingSickLeaves()
      } catch (error) {
        console.error('Error removing sick leave:', error)
        showMessage('Failed to remove sick leave', 'error')
      }
    }
  }
  showConfirmModal.value = true
}

function confirmAction() {
  if (confirmModalData.value.onConfirm) {
    confirmModalData.value.onConfirm()
  }
  showConfirmModal.value = false
}

async function loadShifts() {
  try {
    loading.value = true
    // Calculate date range
    const endDate = new Date()
    let startDate = new Date()
    if (dateRange.value === 'week') {
      startDate.setDate(endDate.getDate() - 7)
    } else if (dateRange.value === 'month') {
      startDate.setDate(endDate.getDate() - 30)
    } else {
      startDate.setDate(endDate.getDate() - 90)
    }
    // Use timeEntryApi to fetch time entries for approval
    const status = statusFilter.value === 'pending' ? 'submitted_pending' : statusFilter.value
    const entries = await timeEntryApi.getTimeEntries({
      start_date: startDate.toISOString().split('T')[0],
      end_date: endDate.toISOString().split('T')[0],
      status: status !== 'all' ? status : undefined
    })
    allShifts.value = entries
  } catch (error) {
    console.error('Error loading time entries:', error)
    showMessage('Failed to load time entries', 'error')
  } finally {
    loading.value = false
  }
}

function refreshData() {
  loadShifts()
}

function filterShifts() {
  // Filtering is handled by computed property
}

async function approveShift(shift) {
  try {
    loading.value = true
    await timeEntryApi.updateShiftStatus(shift.id, 'approved', shift.user_id)
    
    // Update local state
    const shiftIndex = allShifts.value.findIndex(s => s.id === shift.id)
    if (shiftIndex !== -1) {
      allShifts.value[shiftIndex].status = 'approved'
    }
    
    showMessage(`Shift approved for ${shift.worker_name}`, 'success')
  } catch (error) {
    console.error('Error approving shift:', error)
    showMessage('Failed to approve shift', 'error')
  } finally {
    loading.value = false
  }
}

async function rejectShift(shift) {
  try {
    loading.value = true
    await timeEntryApi.updateShiftStatus(shift.id, 'rejected', shift.user_id)
    
    // Update local state
    const shiftIndex = allShifts.value.findIndex(s => s.id === shift.id)
    if (shiftIndex !== -1) {
      allShifts.value[shiftIndex].status = 'rejected'
    }
    
    showMessage(`Shift rejected for ${shift.worker_name}`, 'success')
  } catch (error) {
    console.error('Error rejecting shift:', error)
    showMessage('Failed to reject shift', 'error')
  } finally {
    loading.value = false
  }
}

async function bulkApproveAll() {
  try {
    loading.value = true
    
    const pendingShifts = allShifts.value.filter(shift => 
      shift.status === 'submitted_pending' || shift.status === 'submitted_ok'
    )
    
    for (const shift of pendingShifts) {
      await timeEntryApi.updateShiftStatus(shift.id, 'approved', shift.user_id)
      
      // Update local state
      const shiftIndex = allShifts.value.findIndex(s => s.id === shift.id)
      if (shiftIndex !== -1) {
        allShifts.value[shiftIndex].status = 'approved'
      }
    }
    
    showMessage(`Approved ${pendingShifts.length} shifts`, 'success')
  } catch (error) {
    console.error('Error bulk approving:', error)
    showMessage('Failed to approve shifts', 'error')
  } finally {
    loading.value = false
  }
}

function viewWorkerDetails(shift) {
  router.push(`/worker/${shift.user_id}`)
}

// Show discrepancy modal and load details if needed
async function showDiscrepancyModal(shift) {
  hoveredShiftId.value = shift.id
  
  // If details not already loaded, load them
  if (!shift.mismatchDetails) {
    const details = await getMismatchDetails(shift)
    if (details) {
      // Find the shift in allShifts and update it there to trigger reactivity
      const shiftIndex = allShifts.value.findIndex(s => s.id === shift.id)
      if (shiftIndex !== -1) {
        allShifts.value[shiftIndex].mismatchDetails = details
        allShifts.value[shiftIndex].mismatchTooltip = formatMismatchTooltip(details)
      }
    } else {
    }
  }
}

// Get detailed mismatch information for hover tooltip
async function getMismatchDetails(shift) {
  // For submitted_pending shifts, show detailed comparison
  const details = {
    reportedStart: shift.start_time.includes(':') ? shift.start_time.substring(0, 5) : shift.start_time,
    reportedEnd: shift.end_time.includes(':') ? shift.end_time.substring(0, 5) : shift.end_time,
    reportedBreak: shift.break_duration || 0,
    assignedStart: null,
    assignedEnd: null,
    assignedDuration: null,
    reportedDuration: null,
    requiredBreak: null
  }
  
  // Calculate reported duration
  const reportedStartMin = parseTimeToMinutes(shift.start_time)
  const reportedEndMin = parseTimeToMinutes(shift.end_time)
  let reportedDuration = reportedEndMin - reportedStartMin
  if (reportedDuration < 0) reportedDuration += 24 * 60
  reportedDuration -= (shift.break_duration || 0)
  details.reportedDuration = Math.round(reportedDuration)
  
  // Get assigned shift for this specific user and date using the shift API
  try {
    const userId = shift.user?.id || shift.user_id
    
    // Fetch the assigned shift for this user and date
    const assignedShifts = await shiftApi.getWorkerShiftsInRange(
      userId, 
      new Date(shift.date), 
      new Date(shift.date)
    )
    
    let assignedShift = null
    if (assignedShifts && assignedShifts.length > 0) {
      // Get the shift details from the assignment
      const assignment = assignedShifts[0]
      assignedShift = assignment.shift_details || assignment.shift
    }
    
    if (assignedShift) {
      // Extract time from datetime strings (if they contain 'T') or use as-is if already time format
      details.assignedStart = assignedShift.start_time.includes('T') 
        ? assignedShift.start_time.split('T')[1].substring(0, 5)
        : assignedShift.start_time
      details.assignedEnd = assignedShift.end_time.includes('T')
        ? assignedShift.end_time.split('T')[1].substring(0, 5) 
        : assignedShift.end_time
      
      // Calculate assigned duration (assigned shifts already have breaks built in, so just get total time)
      const assignedStartMin = parseTimeToMinutes(details.assignedStart)
      const assignedEndMin = parseTimeToMinutes(details.assignedEnd)
      let assignedDuration = assignedEndMin - assignedStartMin
      if (assignedDuration < 0) assignedDuration += 24 * 60
      details.assignedDuration = Math.round(assignedDuration)
    }
  } catch (error) {
    console.error('Error finding assigned shift:', error)
  }
  
  // Calculate required break
  const shiftDuration = calculateShiftDuration(shift)
  details.requiredBreak = shiftDuration >= 5.5 ? 30 : 0
  
  // Calculate mismatch flags for the template
  // Only show shift mismatch if we have assigned times AND there's actually a meaningful mismatch
  details.hasShiftMismatch = details.assignedStart && details.assignedEnd && 
    (details.assignedStart !== details.reportedStart || 
     details.assignedEnd !== details.reportedEnd) &&
    Math.abs(details.reportedDuration - details.assignedDuration) > 5
  
  details.hasBreakDiscrepancy = details.reportedBreak !== details.requiredBreak
  
  return details
}

function formatMismatchTooltip(details) {
  if (!details) return ''
  
  let tooltip = []
  
  // Show shift time comparison if assigned shift exists
  if (details.hasShiftMismatch && details.assignedStart && details.assignedEnd) {
    tooltip.push('⏰ SHIFT TIME MISMATCH:')
    tooltip.push(`  Assigned: ${details.assignedStart} - ${details.assignedEnd} (${details.assignedDuration} min)`)
    tooltip.push(`  Reported: ${details.reportedStart} - ${details.reportedEnd} (${details.reportedDuration} min)`)
    const timeDiff = Math.abs(details.reportedDuration - details.assignedDuration)
    const moreOrLess = details.reportedDuration > details.assignedDuration ? 'MORE' : 'LESS'
    tooltip.push(`  Time Difference: ${timeDiff} minutes ${moreOrLess} than assigned`)
  }
  
  // Show time discrepancy even if no assigned shift exists
  if (details.hasShiftMismatch && (!details.assignedStart || !details.assignedEnd)) {
    tooltip.push('⏰ REPORTED TIME:')
    tooltip.push(`  Shift: ${details.reportedStart} - ${details.reportedEnd} (${details.reportedDuration} min)`)
  }
  
  // Show break discrepancy
  if (details.hasBreakDiscrepancy) {
    if (tooltip.length > 0) tooltip.push('')
    tooltip.push('☕ BREAK DISCREPANCY:')
    tooltip.push(`  Required: ${details.requiredBreak} minutes`)
    tooltip.push(`  Reported: ${details.reportedBreak} minutes`)
    if (details.reportedBreak !== details.requiredBreak) {
      if (details.reportedBreak < details.requiredBreak) {
        tooltip.push(`  Missing: ${details.requiredBreak - details.reportedBreak} minutes`)
      } else {
        tooltip.push(`  Extra break: ${details.reportedBreak - details.requiredBreak} minutes`)
      }
    }
  }
  
  return tooltip.join('\n')
}

function calculateShiftHours(shift) {
  if (!shift.start_time || !shift.end_time) return '0.00'
  
  const start = new Date(`2000-01-01 ${shift.start_time}`)
  const end = new Date(`2000-01-01 ${shift.end_time}`)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  const diffMs = end - start - (breakMinutes * 60 * 1000)
  const hours = diffMs / (1000 * 60 * 60)
  
  return Math.max(0, hours).toFixed(2)
}

function calculateShiftDuration(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const start = new Date(`2000-01-01 ${shift.start_time}`)
  const end = new Date(`2000-01-01 ${shift.end_time}`)
  
  // Handle overnight shifts
  if (end <= start) {
    end.setDate(end.getDate() + 1)
  }
  
  const diffMs = end - start
  const hours = diffMs / (1000 * 60 * 60)
  
  return Math.max(0, hours)
}

function hasOBHours(shift) {
  return getOB1Hours(shift) > 0 || getOB2Hours(shift) > 0 || getOB3Hours(shift) > 0
}

function getOB1Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay()
  
  if (dayOfWeek === 6 || dayOfWeek === 0) return 0 // Weekend, no OB1
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  const ob1Start = 18 * 60 + 15 // 18:15
  const ob1End = 20 * 60 // 20:00
  
  let ob1Minutes = 0
  
  if (end <= start) {
    if (start < ob1End) {
      const dayEnd = Math.min(end + 24 * 60, ob1End)
      ob1Minutes += Math.max(0, dayEnd - Math.max(start, ob1Start))
    }
  } else {
    const overlapStart = Math.max(start, ob1Start)
    const overlapEnd = Math.min(end, ob1End)
    ob1Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob1Minutes = Math.max(0, ob1Minutes - (breakMinutes * ob1Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob1Minutes / 60) * 4) / 4
}

function getOB2Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay()
  
  if (dayOfWeek === 6 || dayOfWeek === 0) return 0 // Weekend covered by OB3
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  const ob2Start = 20 * 60 // 20:00
  const ob2End = 23 * 60 + 59 // 23:59
  
  let ob2Minutes = 0
  
  if (end <= start) {
    if (start <= ob2End) {
      ob2Minutes += Math.max(0, ob2End - Math.max(start, ob2Start))
    }
  } else {
    const overlapStart = Math.max(start, ob2Start)
    const overlapEnd = Math.min(end, ob2End)
    ob2Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob2Minutes = Math.max(0, ob2Minutes - (breakMinutes * ob2Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob2Minutes / 60) * 4) / 4
}

function getOB3Hours(shift) {
  if (!shift.start_time || !shift.end_time) return 0
  
  const date = new Date(shift.date)
  const dayOfWeek = date.getDay()
  
  let ob3StartTime = 0
  let ob3EndTime = 24 * 60 - 1
  let isOB3 = false
  
  if (dayOfWeek === 6) {
    isOB3 = true
    ob3StartTime = 12 * 60 // 12:00
  } else if (dayOfWeek === 0) {
    isOB3 = true
    ob3StartTime = 0
  }
  
  if (!isOB3) return 0
  
  const start = parseTimeToMinutes(shift.start_time)
  const end = parseTimeToMinutes(shift.end_time)
  const breakMinutes = parseInt(shift.break_duration) || 0
  
  let ob3Minutes = 0
  
  if (end <= start) {
    if (start >= ob3StartTime) {
      ob3Minutes += Math.max(0, ob3EndTime - start)
    }
    if (end <= ob3EndTime) {
      ob3Minutes += Math.max(0, end - Math.max(0, ob3StartTime))
    }
  } else {
    const overlapStart = Math.max(start, ob3StartTime)
    const overlapEnd = Math.min(end, ob3EndTime)
    ob3Minutes = Math.max(0, overlapEnd - overlapStart)
  }
  
  const totalWorkMinutes = end <= start ? (end + 24 * 60 - start) : (end - start)
  if (totalWorkMinutes > 0) {
    ob3Minutes = Math.max(0, ob3Minutes - (breakMinutes * ob3Minutes / totalWorkMinutes))
  }
  
  return Math.round((ob3Minutes / 60) * 4) / 4
}

function parseTimeToMinutes(timeString) {
  const [hours, minutes] = timeString.split(':').map(Number)
  return hours * 60 + minutes
}

function formatDate(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })
}

function formatWeekday(dateStr) {
  const date = new Date(dateStr + 'T00:00:00')
  return date.toLocaleDateString('sv-SE', { weekday: 'short' })
}

function formatDateTime(dateTimeStr) {
  if (!dateTimeStr) return ''
  const date = new Date(dateTimeStr)
  return date.toLocaleDateString('sv-SE', { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function exportAllReports() {
  showMessage('Export functionality coming soon', 'info')
}

function showMessage(text, type = 'success') {
  message.value = text
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 3000)
}

function managerShiftMismatch(shift) {
  // Try to use assigned_start_time/assigned_end_time if present
  let assignedStart = shift.assigned_start_time;
  let assignedEnd = shift.assigned_end_time;
  
  // Fallback: if not present, try to find assigned shift for this user/date from shift.assignedShifts or similar
  if (!assignedStart || !assignedEnd) {
    if (shift.assignedShifts && shift.assignedShifts.length > 0) {
      // Find closest assigned shift by start/end time
      const entryStartMin = parseTimeToMinutes(shift.start_time);
      const entryEndMin = parseTimeToMinutes(shift.end_time);
      let closestShift = shift.assignedShifts.reduce((best, s) => {
        const sStartMin = parseTimeToMinutes(s.start_time);
        const sEndMin = parseTimeToMinutes(s.end_time);
        const diff = Math.abs(sStartMin - entryStartMin) + Math.abs(sEndMin - entryEndMin);
        if (!best || diff < best.diff) {
          return { diff, start: s.start_time, end: s.end_time };
        }
        return best;
      }, null);
      if (closestShift) {
        assignedStart = closestShift.start;
        assignedEnd = closestShift.end;
      }
    }
  }
  
  if (!shift.start_time || !shift.end_time || !assignedStart || !assignedEnd) {
    return false;
  }
  
  const entryStartMin = parseTimeToMinutes(shift.start_time);
  const entryEndMin = parseTimeToMinutes(shift.end_time);
  let entryDuration = entryEndMin - entryStartMin;
  if (entryDuration < 0) entryDuration += 24 * 60;
  entryDuration -= parseInt(shift.break_duration) || 0;
  
  const assignedStartMin = parseTimeToMinutes(assignedStart);
  const assignedEndMin = parseTimeToMinutes(assignedEnd);
  let assignedDuration = assignedEndMin - assignedStartMin;
  if (assignedDuration < 0) assignedDuration += 24 * 60;
  
  // Allow a small tolerance (e.g. 5 minutes)
  const mismatch = Math.abs(entryDuration - assignedDuration) > 5;
  
  return mismatch;
}
</script>

<style scoped>
.manager-dashboard {
  padding: 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;
}

.header h2 {
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.export-btn, .refresh-btn, .create-shift-btn, .settings-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.settings-btn {
  background: linear-gradient(135deg, #4CAF50 0%, #45a049 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);
}

.settings-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(76, 175, 80, 0.4);
}

.create-shift-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
}

.create-shift-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
}

.export-btn {
  background: #28a745;
  color: white;
}

.export-btn:hover:not(:disabled) {
  background: #218838;
}

.refresh-btn {
  background: #17a2b8;
  color: white;
}

.refresh-btn:hover:not(:disabled) {
  background: #138496;
}

/* Stats Summary */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 1px solid #e0e0e0;
}

.stat-card.rejected {
  border-left: 4px solid #e74c3c;
}

.stat-card.rejected .stat-value {
  color: #e74c3c;
}

.stat-card.sick-leave {
  border-left: 4px solid #ef4444;
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card.sick-leave:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

.stat-card.sick-leave .stat-value {
  color: #ef4444;
}

.stat-label {
  display: block;
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.5rem;
}

.stat-value {
  display: block;
  font-size: 1.8rem;
  font-weight: bold;
  color: #333;
}

/* Filters */
.filters {
  display: flex;
  gap: 2rem;
  align-items: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.filter-group label {
  font-weight: bold;
  color: #555;
}

.filter-group select {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.bulk-approve-btn {
  padding: 0.75rem 1.5rem;
  background: #28a745;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  transition: all 0.2s ease;
}

.bulk-approve-btn:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
}

/* Shifts Section */
.shifts-section h3 {
  color: rgba(255, 255, 255, 0.95);
  margin-bottom: 1.5rem;
}

.shifts-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.shift-card {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.shift-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.shift-card.status-submitted-ok {
  border-left: 4px solid #4caf50;
  background: #f0fff4;
}

.shift-card.status-submitted-pending {
  border-left: 4px solid #ff9800;
  background: #fff8f0;
}

.shift-card.status-approved {
  border-left: 4px solid #2196f3;
  background: #f8f9ff;
}

.shift-card.status-rejected {
  border-left: 4px solid #f44336;
  background: #fff8f8;
}

.shift-card.has-discrepancy {
  background: repeating-linear-gradient(
    135deg,
    #fff3e0,
    #fff3e0 10px,
    #ffe0b2 10px,
    #ffe0b2 20px
  ) !important;
  border-left: 4px solid #ff9800 !important;
}

.shift-card.has-break-discrepancy:hover {
  background: repeating-linear-gradient(
    135deg,
    #ffcdd2,
    #ffcdd2 10px,
    #ef9a9a 10px,
    #ef9a9a 20px
  ) !important;
}

.shift-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1.5fr 1fr 2fr;
  gap: 1rem;
  align-items: center;
  margin-bottom: 1rem;
}

.worker-info h4 {
  margin: 0 0 0.25rem 0;
  color: #333;
  font-size: 1.1rem;
}

.role-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: capitalize;
  background: #e3f2fd;
  color: #1976d2;
}

.shift-date {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.date {
  font-weight: bold;
  font-size: 1.1rem;
  color: #333;
}

.weekday {
  font-size: 0.9rem;
  color: #666;
}

.shift-times {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-range {
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
}

.shift-hours {
  font-size: 0.9rem;
  color: #2196f3;
  font-weight: bold;
}

.shift-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
}

.status-badge {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
}

.status-badge.submitted_pending:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(255, 152, 0, 0.3);
}

.status-badge.submitted_pending {
  background: #fff3cd;
  color: #856404;
  font-weight: 600;
}

.status-badge.submitted_ok {
  background: #d1f2eb;
  color: #0f6648;
  font-weight: 600;
}

.status-badge.approved {
  background: #cce5ff;
  color: #004085;
}

.status-badge.rejected {
  background: #f8d7da;
  color: #842029;
}

.submitted-time {
  font-size: 0.8rem;
  color: #666;
}

.break-discrepancy-warning {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #ffebee;
  border: 1px solid #f44336;
  border-radius: 4px;
  color: #c62828;
  font-size: 0.9rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  grid-column: 1 / -1;
  position: relative;
  white-space: pre-line;
}

.discrepancy-hover {
  cursor: help;
  transition: all 0.2s ease;
}

.discrepancy-hover:hover {
  background-color: #ffcdd2;
  border-color: #e53935;
  box-shadow: 0 2px 8px rgba(244, 67, 54, 0.2);
}

.hover-hint {
  font-size: 0.75rem;
  color: #f44336;
  font-weight: normal;
  padding: 0.2rem 0.5rem;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 3px;
  border: 1px solid #f44336;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.discrepancy-hover:hover .hover-hint {
  opacity: 1;
}

/* Hover Modal for Discrepancies */
.discrepancy-modal {
  position: absolute;
  top: 50%;
  right: calc(100% + 1rem);
  transform: translateY(-50%);
  width: 380px;
  max-width: 90vw;
  background: white;
  border: 2px solid #ff9800;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  z-index: 1001;
  animation: fadeInModal 0.2s ease;
  pointer-events: auto;
}

@keyframes fadeInModal {
  from {
    opacity: 0;
    transform: translateY(-50%) translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
  }
}

.modal-arrow {
  position: absolute;
  top: 50%;
  right: -8px;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  border-left: 8px solid #ff9800;
}

.modal-arrow::after {
  content: '';
  position: absolute;
  top: -7px;
  right: 2px;
  width: 0;
  height: 0;
  border-top: 7px solid transparent;
  border-bottom: 7px solid transparent;
  border-left: 7px solid white;
}

/* Responsive positioning for mobile */
@media (max-width: 768px) {
  .discrepancy-modal {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 90vw;
    max-width: 350px;
    right: auto;
  }
  
  .modal-arrow {
    display: none;
  }
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #ff9800;
  color: #e65100;
  font-size: 1rem;
}

.warning-icon {
  font-size: 1.2rem;
}

.modal-content {
  padding: 1rem;
}

.modal-section {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 4px solid #ff9800;
}

.modal-section:first-child {
  margin-top: 0;
}

.modal-section.shift-mismatch {
  border-left-color: #2196f3;
  background: #f3f8ff;
}

.modal-section.break-discrepancy {
  border-left-color: #ff5722;
  background: #fff8f5;
}

.section-title {
  font-weight: bold;
  color: #424242;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
}

.section-details {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.3rem 0.5rem;
  background: #f5f5f5;
  border-radius: 4px;
  font-size: 0.85rem;
}

.detail-row .label {
  font-weight: 600;
  color: #616161;
  min-width: 80px;
}

.detail-row .value {
  color: #424242;
  font-family: 'Courier New', monospace;
}

.detail-row .value.highlight {
  color: #d32f2f;
  /* font-weight: bold; */
}

.detail-row.difference {
  background: #ffebee;
  border: 1px solid #ef5350;
}

.detail-row.difference .label {
  color: #c62828;
}

.detail-row.difference .value {
  color: #c62828;
  font-weight: bold;
}

.shift-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.action-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  font-weight: bold;
}

.action-btn.approve {
  background: #28a745;
  color: white;
}

.action-btn.approve:hover:not(:disabled) {
  background: #218838;
}

.action-btn.reject {
  background: #dc3545;
  color: white;
}

.action-btn.reject:hover:not(:disabled) {
  background: #c82333;
}

.action-btn.view {
  background: #6c757d;
  color: white;
}

.action-btn.view:hover:not(:disabled) {
  background: #5a6268;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.shift-details {
  margin-top: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #2196f3;
}

.description {
  margin: 0;
  color: #333;
  font-style: italic;
}

.shift-breakdown {
  margin-top: 1rem;
  padding: 0.75rem;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
}

.ob-hours {
  display: flex;
  gap: 1rem;
  justify-content: center;
}

.ob1, .ob2, .ob3 {
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
}

.ob1 {
  background: #fff3cd;
  color: #856404;
}

.ob2 {
  background: #f8d7da;
  color: #842029;
}

.ob3 {
  background: #e2e3f3;
  color: #383d41;
}

.loading, .no-data {
  text-align: center;
  padding: 3rem;
  color: rgba(65, 0, 102, 0.6);
}

.message {
  position: fixed;
  top: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 6px;
  color: white;
  font-weight: bold;
  z-index: 1000;
  animation: slideIn 0.3s ease;
}

.message.success {
  background: #4caf50;
}

.message.error {
  background: #f44336;
}

.message.info {
  background: #2196f3;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .shift-header {
    grid-template-columns: 1fr;
    gap: 0.75rem;
    text-align: center;
  }
  
  .shift-actions {
    justify-content: center;
  }
}

@media (max-width: 768px) {
  .manager-dashboard {
    padding: 1rem;
  }
  
  .header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .header-actions {
    justify-content: center;
  }
  
  .filters {
    flex-direction: column;
    gap: 1rem;
  }
  
  .stats-summary {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .shift-card {
    padding: 1rem;
  }
  
  .action-btn {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }
}

/* Sick Leave Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.sick-leave-modal {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 2px solid #ef4444;
  background: #fee2e2;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  color: #991b1b;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #991b1b;
  padding: 0.25rem 0.5rem;
  transition: background 0.2s;
  border-radius: 4px;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.1);
}

.modal-body {
  padding: 1.5rem;
  max-height: calc(80vh - 100px);
  overflow-y: auto;
}

.sick-leave-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.sick-leave-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #fee2e2;
  border-left: 4px solid #ef4444;
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.sick-leave-item:hover {
  transform: translateX(4px);
  box-shadow: 0 2px 8px rgba(239, 68, 68, 0.3);
}

.sick-worker-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.worker-name {
  font-weight: 600;
  color: #991b1b;
  font-size: 1.1rem;
}

.sick-date {
  color: #b91c1c;
  font-size: 0.9rem;
}

.sick-leave-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sick-icon {
  font-size: 2rem;
}

.remove-sick-leave-btn {
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  padding: 0;
  line-height: 1;
}

.remove-sick-leave-btn:hover {
  background: #991b1b;
  transform: scale(1.1);
}

.remove-sick-leave-btn:active {
  transform: scale(0.95);
}

/* Confirmation Modal */
.confirm-modal {
  background: white;
  border-radius: 12px;
  padding: 0;
  max-width: 450px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
}

.confirm-modal .modal-header {
  padding: 1.5rem;
  border-bottom: 2px solid #ddd;
  background: #f9fafb;
}

.confirm-modal .modal-header h3 {
  margin: 0;
  font-size: 1.3rem;
  color: #1f2937;
}

.confirm-modal .modal-body {
  padding: 1.5rem;
}

.confirm-modal .modal-body p {
  margin: 0;
  color: #4b5563;
  font-size: 1rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
}

.btn-secondary {
  padding: 0.625rem 1.25rem;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-secondary:hover {
  background: #4b5563;
}

.btn-danger {
  padding: 0.625rem 1.25rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: background 0.2s;
}

.btn-danger:hover {
  background: #991b1b;
}
</style>